import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import quizApi from '../utils/quizApi';
import DocumentPicker from '../components/DocumentPicker';
import DocKeyLink from '../components/DocKeyLink';
import GenerateQuizModal from '../components/GenerateQuizModal';
import QuizResults from '../components/QuizResults';
import '../styles/Quiz.css';

const blankOption = () => ({ text: '', is_correct: false });
const blankQuestion = () => ({
  text: '', kind: 'single', origin: 'human',
  options: [blankOption(), blankOption()],
});

/**
 * Author one quiz.
 *
 * `/training/quizzes/new` opens the document picker; picking a document creates
 * the quiz immediately and lands on its own URL, so the questions are never held
 * in a browser tab against a document that hasn't been claimed yet.
 *
 * A quiz is EDITABLE until the first person starts it — publishing only makes it
 * visible to trainees. Fixing a typo before anyone has sat it shouldn't cost a
 * whole revision. Once one trainee starts, the questions are part of somebody's
 * record and freeze for good; the screen then says who froze it and offers
 * "Start next revision" where Save used to be.
 */
function QuizBuilder() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isNew = !quizId;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorLink, setErrorLink] = useState(null);
  const [flash, setFlash] = useState('');
  const [problems, setProblems] = useState([]);

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([blankQuestion()]);
  const [stats, setStats] = useState(null);
  const [editable, setEditable] = useState(true);
  const [hasAttempts, setHasAttempts] = useState(false);
  const [reach, setReach] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const d = await quizApi.get(quizId);
      setQuiz(d.quiz);
      setQuestions((d.questions || []).length ? d.questions : [blankQuestion()]);
      setStats(d.stats);
      setEditable(d.editable);
      setHasAttempts(d.has_attempts);
      // `url` is the DOCUMENT's link in Orcanos, not the quiz's — it arrives with the
      // reach verdict because both come from the same report lookup (the `quizzes`
      // table stores only the key).
      setReach({ state: d.reach, revision: d.doc_revision, message: d.reach_message,
                 url: d.doc_url });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [quizId, isNew]);

  useEffect(() => { load(); }, [load]);

  /* ------------------------------------------------------------ new quiz */

  const pickDocument = async (doc) => {
    setSaving(true);
    setError('');
    setErrorLink(null);
    try {
      const created = await quizApi.create({ doc_key: doc.key, setup_id: doc.panel_id });
      navigate(`/training/quizzes/${created.id}`, { replace: true });
    } catch (e) {
      setError(e.message);
      // "already has a quiz" carries the id — offer the link rather than a dead end.
      if (e.detail?.quiz_id) setErrorLink(e.detail.quiz_id);
    } finally {
      setSaving(false);
    }
  };

  if (isNew) {
    return (
      <div className="qz-page">
        <button className="qz-back" onClick={() => navigate('/training/quizzes')}>← Quizzes</button>
        <div className="qz-head">
          <div>
            <h2>New quiz</h2>
            <p className="qz-sub">
              Choose the document this quiz is for. Its name and approved revision come
              straight from the training report, so the quiz and the compliance grid can
              never disagree about which revision people owe.
            </p>
          </div>
        </div>
        {error && (
          <div className="qz-note qz-note-err">
            <span>{error}</span>
            {errorLink && (
              <button
                className="qz-btn qz-btn-ghost qz-btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => navigate(`/training/quizzes/${errorLink}`)}
              >Open it →</button>
            )}
          </div>
        )}
        <DocumentPicker onPick={pickDocument} />
      </div>
    );
  }

  /* ------------------------------------------------------------ existing */

  const setQ = (i, patch) =>
    setQuestions((qs) => qs.map((q, n) => (n === i ? { ...q, ...patch } : q)));

  const setOpt = (qi, oi, patch) =>
    setQuestions((qs) => qs.map((q, n) => {
      if (n !== qi) return q;
      return { ...q, options: q.options.map((o, m) => (m === oi ? { ...o, ...patch } : o)) };
    }));

  /* Marking a correct answer on a SINGLE-answer question clears the others. Two
     marked answers make a quiz that can't be published, and the author would only
     find out at Publish — long after forgetting which question it was. */
  const markCorrect = (qi, oi) =>
    setQuestions((qs) => qs.map((q, n) => {
      if (n !== qi) return q;
      const options = q.options.map((o, m) => ({
        ...o,
        is_correct: q.kind === 'multi'
          ? (m === oi ? !o.is_correct : o.is_correct)
          : m === oi,
      }));
      return { ...q, options };
    }));

  const save = async () => {
    setSaving(true);
    setError('');
    setFlash('');
    try {
      await quizApi.update(quizId, {
        title: quiz.title,
        pass_mark: Number(quiz.pass_mark),
        max_attempts: Number(quiz.max_attempts),
        cooloff_hours: Number(quiz.cooloff_hours),
      });
      const res = await quizApi.saveQuestions(quizId, questions);
      setProblems(res.problems || []);
      setFlash('Saved.');
      load();
    } catch (e) {
      setError(e.message);
      // The refusal usually means somebody just started it — reload so the screen
      // stops offering edits it can no longer make.
      load();
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!window.confirm(
      'Publish this quiz?\n\nTrainees who owe this document will see it on My to-do '
      + 'straight away, and can sit it once they have signed their training task for '
      + 'this revision.\n\nYou can still edit it until the first person starts it — after '
      + 'that its questions freeze, because their records have to keep meaning what they meant.'
    )) return;
    setSaving(true);
    setError('');
    try {
      await quizApi.saveQuestions(quizId, questions);
      await quizApi.publish(quizId);
      setFlash('Published. Trainees can take it now.');
      setProblems([]);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const nextRevision = async () => {
    // Pre-filled with the revision the REPORT calls current, because that string is
    // what a trainee's row is keyed on. A typed revision that doesn't match one the
    // report produced strands the new quiz exactly as the old one is stranded.
    const suggested = reach?.revision || '';
    const rev = window.prompt(
      'Which revision is the new quiz for?\n\n'
      + (suggested
        ? `The training report currently has ${quiz.doc_key} at revision ${suggested}. `
          + 'Trainees are offered the quiz filed under that revision, so leave this as it is '
          + 'unless you are preparing one ahead of time.\n\n'
        : '')
      + 'It starts as an editable copy of this one. Publishing it retires this quiz; '
      + 'attempts already taken keep pointing at the quiz they were sat.',
      suggested
    );
    if (!rev || !rev.trim()) return;
    try {
      const d = await quizApi.copyForward(quizId, rev.trim(), quiz.title);
      navigate(`/training/quizzes/${d.id}`);
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this quiz and its questions?')) return;
    try {
      await quizApi.remove(quizId);
      navigate('/training/quizzes');
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  if (loading) return <div className="qz-page"><div className="qz-loading">Loading…</div></div>;
  if (!quiz) {
    return (
      <div className="qz-page">
        <div className="qz-empty">
          <b>Quiz not found</b>
          <span>{error || 'It may have been deleted.'}</span>
          <button className="qz-btn qz-btn-ghost" onClick={() => navigate('/training/quizzes')}>
            Back to quizzes
          </button>
        </div>
      </div>
    );
  }

  const people = stats?.people || 0;
  // Published, but filed under a revision the report has moved past — so it matches
  // no trainee at all. It is a warning rather than a block: carrying questions to a
  // new revision is an authoring decision, not something the app should do quietly.
  const stranded = reach?.state === 'stale_revision';

  /* The tab is derived from the URL, never held in state. Two reasons, and the second
     is the one that bites: a link to this quiz's results has to survive being pasted
     to somebody else, and choosing a default tab inside the async load is precisely
     the pattern Critical Note #16 exists about — the load re-fires when its data
     lands and throws the author back out of the tab they just opened.

     A quiz people are already sitting opens on Results, because that is what its
     author came to see; an unstarted one opens on the questions they came to write. */
  const tab = params.get('tab') || (hasAttempts ? 'results' : 'questions');
  const setTab = (next) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  return (
    <div className="qz-page">
      <button className="qz-back" onClick={() => navigate('/training/quizzes')}>← Quizzes</button>

      <div className="qz-head">
        <div>
          <h2>{quiz.title || quiz.doc_key}</h2>
          <p className="qz-sub">
            <DocKeyLink docKey={quiz.doc_key} url={reach?.url} />
            {quiz.revision ? <> · revision <b>{quiz.revision}</b></> : null}
            {' · '}{quiz.status}
            {editable ? ' · editable' : ' · frozen'}
          </p>
        </div>
        <div className="qz-head-actions">
          {editable ? (
            <>
              <button className="qz-btn qz-btn-danger" onClick={remove} disabled={saving}>
                Delete
              </button>
              {/* Only for a document quiz: a formal training record (TRN) has no revision
                  and no attached file, so there is nothing to write questions from. The
                  backend refuses it too — this just doesn't offer a button that can't work. */}
              {(quiz.src || 'doc') === 'doc' && quiz.revision && (
                <button className="qz-btn qz-btn-ghost" onClick={() => setGenerating(true)}
                        disabled={saving}>
                  ✨ Generate quiz
                </button>
              )}
              <button className="qz-btn qz-btn-ghost" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {quiz.status !== 'published' && (
                <button className="qz-btn qz-btn-primary" onClick={publish} disabled={saving}>
                  Publish
                </button>
              )}
              {/* A PUBLISHED quiz nobody has started is still editable, so it used to
                  show no way across to a new revision at all — and a stranded one is
                  usually exactly that: published, never sat, and now pointing at a
                  revision the report has moved past. */}
              {quiz.status === 'published' && (
                <button
                  className={`qz-btn ${stranded ? 'qz-btn-primary' : 'qz-btn-ghost'}`}
                  onClick={nextRevision}
                  disabled={saving}
                >
                  Start next revision
                </button>
              )}
            </>
          ) : (
            <button className="qz-btn qz-btn-primary" onClick={nextRevision}>
              Start next revision
            </button>
          )}
        </div>
      </div>

      {generating && (
        <GenerateQuizModal
          quiz={quiz}
          /* `added` is true when the run put questions in before the window was closed —
             the editor is then holding a stale list and must re-read, or the next Save
             would write the old one back over them. */
          onClose={(added) => { setGenerating(false); if (added) load(); }}
          onDone={(res) => {
            setGenerating(false);
            if (res?.questions_added) {
              setFlash(`${res.questions_added} AI draft question${res.questions_added === 1 ? '' : 's'} added — review them before publishing.`);
            }
            load();
          }}
        />
      )}

      {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}
      {flash && <div className="qz-note qz-note-ok"><span>{flash}</span></div>}

      {/* Reaching nobody. This is the module's own blind spot one level up: the quiz
          is published, the list shows it live, and 0 attempts reads as "nobody has
          got to it yet" rather than "nobody can". Said here, with the action. */}
      {reach && reach.state !== 'ok' && reach.state !== 'retired' && (
        <div className="qz-note qz-note-warn">
          <span>⚠</span>
          <div>
            <b>
              {reach.state === 'stale_revision'
                ? `Reaching nobody — the document is now at revision ${reach.revision || '—'}.`
                : reach.state === 'unknown_doc'
                  ? 'Reaching nobody — this document isn’t in a built training report.'
                  : 'Reaching nobody yet — no training report has been built.'}
            </b>{' '}
            {reach.message}
          </div>
        </div>
      )}

      {!editable && (
        <div className="qz-note qz-note-warn">
          <span>🔒</span>
          <div>
            {quiz.status === 'superseded' ? (
              <><b>This quiz has been retired by a later revision.</b> Open the current
              revision’s quiz to make changes.</>
            ) : (
              <>
                <b>
                  {people === 1 ? '1 person has' : `${people} people have`} started this quiz,
                  so its questions are frozen.
                </b>{' '}
                Their records have to keep meaning what they meant. Use <b>Start next
                revision</b> to change anything.
              </>
            )}
          </div>
        </div>
      )}

      {editable && quiz.status === 'published' && (
        <div className="qz-note qz-note-info">
          <span>👁</span>
          <div>
            <b>Published — trainees can see this now.</b> Nobody has started it yet, so you can
            still change it. It freezes the moment the first person begins.
          </div>
        </div>
      )}

      {editable && problems.length > 0 && tab === 'questions' && (
        <div className="qz-note qz-note-warn">
          <span>⚠</span>
          <div>
            <b>Not ready to publish yet:</b>
            <ul>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        </div>
      )}

      <div className="qz-tabs">
        <button className={`qz-tab${tab === 'questions' ? ' active' : ''}`}
                onClick={() => setTab('questions')}>
          Questions<span className="qz-chip-n">{questions.length}</span>
        </button>
        <button className={`qz-tab${tab === 'results' ? ' active' : ''}`}
                onClick={() => setTab('results')}>
          Results
          {/* People, not attempts — three tries ending in a pass is one trained
              person, and a badge reading 3 would be describing effort. */}
          {people > 0 && <span className="qz-chip-n">{people}</span>}
        </button>
      </div>

      {tab === 'results' ? (
        <QuizResults quizId={quizId} />
      ) : (
      <div className="qz-split">
        <div>
          {questions.map((q, qi) => (
            <div className="qz-q" key={q.id || qi}>
              <div className="qz-q-head">
                <span className="qz-q-n">{qi + 1}</span>
                <select
                  className="qz-input"
                  style={{ width: 'auto' }}
                  value={q.kind}
                  disabled={!editable}
                  onChange={(e) => setQ(qi, { kind: e.target.value })}
                >
                  <option value="single">One answer</option>
                  <option value="multi">Several answers</option>
                </select>
                {q.origin === 'ai' && <span className="qz-pill qz-pill-quiz">AI draft</span>}
                <div className="qz-q-tools">
                  {editable && questions.length > 1 && (
                    <button
                      className="qz-iconbtn"
                      title="Remove this question"
                      onClick={() => setQuestions((qs) => qs.filter((_, n) => n !== qi))}
                    >✕</button>
                  )}
                </div>
              </div>

              <textarea
                className="qz-textarea"
                placeholder="What is the question?"
                value={q.text}
                disabled={!editable}
                onChange={(e) => setQ(qi, { text: e.target.value })}
                style={{ marginBottom: 10 }}
              />

              {q.options.map((o, oi) => (
                <div className="qz-opt" key={o.id || oi}>
                  <input
                    type="text"
                    className="qz-input"
                    placeholder={`Option ${oi + 1}`}
                    value={o.text}
                    disabled={!editable}
                    onChange={(e) => setOpt(qi, oi, { text: e.target.value })}
                  />
                  <label className={`qz-opt-mark${o.is_correct ? ' on' : ''}`}>
                    <input
                      type={q.kind === 'multi' ? 'checkbox' : 'radio'}
                      name={`correct-${qi}`}
                      checked={!!o.is_correct}
                      disabled={!editable}
                      onChange={() => markCorrect(qi, oi)}
                    />
                    Correct
                  </label>
                  {editable && q.options.length > 2 && (
                    <button
                      className="qz-iconbtn"
                      title="Remove this option"
                      onClick={() => setQ(qi, { options: q.options.filter((_, m) => m !== oi) })}
                    >✕</button>
                  )}
                </div>
              ))}

              {editable && (
                <button
                  className="qz-btn qz-btn-ghost qz-btn-sm"
                  style={{ marginLeft: 32, marginTop: 4 }}
                  onClick={() => setQ(qi, { options: [...q.options, blankOption()] })}
                >+ Option</button>
              )}
            </div>
          ))}

          {editable && (
            <button
              className="qz-btn qz-btn-ghost"
              style={{ width: '100%' }}
              onClick={() => setQuestions((qs) => [...qs, blankQuestion()])}
            >+ Add question</button>
          )}
        </div>

        <div>
          <div className="qz-panel">
            <div className="qz-panel-head">Attached to</div>
            <div className="qz-panel-body">
              {/* Read-only for the life of the quiz. The document and its revision came
                  from the report; letting them be retyped here is how a quiz ends up
                  describing a revision nobody owes. */}
              <div className="qz-field">
                <label>Document</label>
                <div className="qz-val">
                  {quiz.doc_name || '—'}
                  <div><DocKeyLink docKey={quiz.doc_key} url={reach?.url} /></div>
                </div>
              </div>
              <div className="qz-field">
                <label>Revision</label>
                <div className="qz-val">
                  {quiz.revision
                    ? <span className="qz-pill qz-pill-rev">{quiz.revision}</span>
                    : <span className="qz-meta">No revision — a training record</span>}
                </div>
              </div>
              <div className="qz-field">
                <label>Quiz title</label>
                <input
                  className="qz-input"
                  value={quiz.title || ''}
                  disabled={!editable}
                  onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* What this revision changed — kept on the quiz, not just shown once in the
              dialog that produced it. It is the reason the revision needs retraining, so
              it belongs beside the questions while they are being written, and it stays
              readable long after the generation job has aged out. */}
          {quiz.change_summary && (
            <div className="qz-panel">
              <div className="qz-panel-head">
                Changed in revision {quiz.revision}
                {quiz.change_summary.previous_revision && (
                  <span className="qz-meta" style={{ fontWeight: 400 }}>
                    {' '}· vs {quiz.change_summary.previous_revision}
                  </span>
                )}
              </div>
              <div className="qz-panel-body">
                {quiz.change_summary.headline && (
                  <p className="qz-gen-headline">{quiz.change_summary.headline}</p>
                )}

                {quiz.change_summary.no_substantive_change ? (
                  <div className="qz-meta">
                    Nothing substantive changed between these revisions — only formatting or
                    document-control details. Worth asking whether this revision needs
                    retraining at all.
                  </div>
                ) : (
                  <ul className="qz-gen-changes">
                    {(quiz.change_summary.changes || []).map((c, i) => (
                      <li key={i}>
                        <b>{c.area}</b> — {c.what}
                        {c.matters ? <div className="qz-meta">{c.matters}</div> : null}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Said plainly rather than left implied: this is a reading of two files by
                    a model, and the author is the one who signs off what it produced. */}
                <div className="qz-meta qz-cs-foot">
                  Written by AI from the two revisions’ files
                  {quiz.change_summary.generated_at
                    ? ` on ${quiz.change_summary.generated_at.slice(0, 10)}`
                    : null}. Check it against the document before relying on it.
                </div>

                {(quiz.change_summary.warnings || []).length > 0 && (
                  <ul className="qz-gen-warns qz-cs-warns">
                    {quiz.change_summary.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="qz-panel">
            <div className="qz-panel-head">Rules</div>
            <div className="qz-panel-body">
              <div className="qz-field">
                <label>Pass mark (%)</label>
                <input type="number" min="0" max="100" className="qz-input"
                       value={quiz.pass_mark} disabled={!editable}
                       onChange={(e) => setQuiz({ ...quiz, pass_mark: e.target.value })} />
              </div>
              <div className="qz-field">
                <label>Attempts allowed</label>
                <input type="number" min="1" max="20" className="qz-input"
                       value={quiz.max_attempts} disabled={!editable}
                       onChange={(e) => setQuiz({ ...quiz, max_attempts: e.target.value })} />
              </div>
              <div className="qz-field">
                <label>Cool-off between attempts (hours)</label>
                <input type="number" min="0" className="qz-input"
                       value={quiz.cooloff_hours} disabled={!editable}
                       onChange={(e) => setQuiz({ ...quiz, cooloff_hours: e.target.value })} />
              </div>
              <div className="qz-field">
                <label>On pass</label>
                <div className="qz-val qz-meta">
                  The pass is recorded and queued for Orcanos. Passing and being signed
                  are shown as two separate states, so a record that hasn’t landed is
                  never mistaken for one that has.
                </div>
              </div>
            </div>
          </div>

          {hasAttempts && (
            <div className="qz-panel">
              <div className="qz-panel-head">Results</div>
              <div className="qz-panel-body">
                <div className="qz-val">
                  {/* PEOPLE, both sides — `quiz_stats` counts a person once however
                      many tries they took. The denominator here is only the people who
                      have sat it; the Results tab is where it becomes everyone who
                      owes it, which is the number that actually means something. */}
                  <b>{stats.passed}</b> of <b>{stats.people}</b> who have sat it passed
                  {stats.in_progress ? <> · {stats.in_progress} in progress</> : null}
                </div>
                <div className="qz-meta" style={{ marginTop: 4 }}>
                  {stats.attempts} attempt{stats.attempts === 1 ? '' : 's'} in total
                </div>
                <button className="qz-btn qz-btn-ghost qz-btn-sm"
                        style={{ marginTop: 10 }}
                        onClick={() => setTab('results')}>
                  Full results →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default QuizBuilder;
